import type { GettextInterface } from "../../../shared/I18n";
import type Sentiment from "../../../shared/util/salary/Sentiment";
import type { ViewjobQuery } from "../__generated__/viewjob.gqlgenerated";
import type { JobDetailsSectionModel } from "../components/core/ProfileInsights/components/PreferencesCollection/types";

/**
 * Builds the JobDetailsSectionModel from ViewJob GraphQL data
 * @param i18n
 * @param viewjob - The GraphQL ViewJob data
 * @returns The employer model object
 */
export const buildJobDetailsSectionModel = (
  i18n: GettextInterface,
  viewjob: NonNullable<ViewjobQuery["viewjob"]>,
): JobDetailsSectionModel => {
  const jobDescriptionSectionedContent = Object.fromEntries(
    (viewjob.details?.userPreference?.jobDescriptionSectionedContentMap || []).map(
      (item: { contentKey: string; contentValue: string[] }) => [
        item.contentKey,
        item.contentValue,
      ],
    ),
  );

  return {
    contents: jobDescriptionSectionedContent,
    title: i18n.gettext("Job details"),
    highlightedAttributesSectionModels:
      viewjob.details?.userPreference?.highlightedAttributes?.map((attr) => ({
        highlightedAttributes:
          attr.highlightedAttributes?.filter((item): item is string => item !== null) ?? [],
        highlightedAttributesIds:
          attr.highlightedAttributesIds?.filter((item) => item !== null) ?? [],
        label: attr.label ?? "",
        tooltips:
          attr.tooltips?.map((tooltip) => ({
            attribute: tooltip?.attribute ?? "",
            tooltipActive: tooltip?.isTooltipActive ?? false,
            tooltipText: tooltip?.tooltipText ?? "",
          })) ?? [],
      })) || undefined,
    jobTypes:
      viewjob.details?.userPreference?.matchedJobTypes?.map((jobType) => ({
        customClassLabels: jobType.customClassLabels?.filter((item) => item !== null) ?? [],
        customClassSuids: jobType.customClassSuids?.filter((item) => item !== null) ?? [],
        label: jobType.label ?? "",
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        sentiment: (jobType.sentiment as Sentiment) ?? "UNKNOWN",
        suid: jobType.suid ?? "",
      })) || undefined,
    segmentPreferences:
      viewjob.details?.userPreference?.matchedSegmentPreferences
        ?.filter((pref) => pref !== null)
        .map((pref) => ({
          attributes:
            pref.attributes
              ?.filter((attr) => attr !== null)
              .map((attr) => ({
                label: attr.label ?? "",
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
                sentiment: (attr.sentiment as Sentiment) ?? "UNKNOWN",
                suid: attr.suid ?? "",
              })) ?? [],
          displayTitle: pref.displayTitle ?? "",
        })) || undefined,
    shiftsAndSchedule:
      viewjob.details?.userPreference?.matchedShift?.map((shift) => ({
        customClassLabels: shift.customClassLabels?.filter((item) => item !== null) ?? [],
        customClassSuids: shift.customClassSuids?.filter((item) => item !== null) ?? [],
        label: shift.label ?? "",
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        sentiment: (shift.sentiment as Sentiment) ?? "UNKNOWN",
        suid: shift.suid ?? "",
      })) || undefined,
  };
};
